import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Eye, CheckCircle2, XCircle, Loader2, RefreshCw, Package, PackageCheck, Pencil } from "lucide-react";

/**
 * Admin > Purchase Orders tab (iter155 IMS Phase C).
 *
 * List of vendor POs (draft/received/cancelled) with per-line CPV auto-computed
 * server-side. CPV formula (kept in sync with routes/purchase_orders.py):
 *   CPV = (product_cost + testing_cost + misc_cost + shipping_share) / qty
 *   shipping_share = shipping_cost × line_qty / total_qty (full allocation)
 */

const emptyLine = () => ({ product_id: "", variant_mg: "", qty: 1, product_cost: 0, testing_cost: 0, misc_cost: 0, custom_name: "", custom_mg: "" });

function computeCpvPreview(lines, shippingCost) {
  const totalQty = lines.reduce((s, l) => s + Math.max(0, Number(l.qty) || 0), 0);
  return lines.map((l) => {
    const qty = Math.max(0, Number(l.qty) || 0);
    const share = totalQty > 0 ? (Number(shippingCost) || 0) * qty / totalQty : 0;
    const line_total = (Number(l.product_cost) || 0) + (Number(l.testing_cost) || 0) + (Number(l.misc_cost) || 0) + share;
    const cpv = qty > 0 ? line_total / qty : 0;
    return { shipping_share: share, line_total, cpv };
  });
}

export default function AdminPurchaseOrders() {
  const [pos, setPos] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);
  // iter156.14 — when set, the modal is in EDIT mode and submits via PUT.
  const [editingId, setEditingId] = useState(null);

  // iter156.12 — Inline quick-create product from the CPV product picker.
  // Opens a small nested dialog when the admin picks "+ New product…" so
  // they can add a SKU (with mg) without leaving the CPV modal. On save the
  // product list refreshes and the new product is auto-attached to the CPV
  // line that triggered the create.
  const [quickNewOpen, setQuickNewOpen] = useState(false);
  const [quickTargetLineIdx, setQuickTargetLineIdx] = useState(null);
  const [quickSaving, setQuickSaving] = useState(false);
  const emptyQuickNew = () => ({ name: "", sku: "", mg: "", category: "Peptide" });
  const [quickNew, setQuickNew] = useState(emptyQuickNew());

  // Draft PO form state
  const [draft, setDraft] = useState({
    vendor: "",
    po_date: new Date().toISOString().slice(0, 10),
    shipping_cost: 0,
    tier: "supply",
    notes: "",
    lines: [emptyLine()],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [posRes, prodRes] = await Promise.all([
        api.get("/admin/purchase-orders"),
        api.get("/admin/products"),
      ]);
      setPos(posRes.data || []);
      setProducts(prodRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to load purchase orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const productsByTier = useMemo(() => {
    const supply = [], select = [];
    for (const p of products) {
      const t = (p.tier || "supply").toLowerCase();
      if (t === "supply") supply.push(p);
      else if (t === "select") select.push(p);
    }
    return { supply, select };
  }, [products]);

  // iter203.5 — Vendor autocomplete. Distinct vendor names harvested from
  // the existing PO ledger (already in state via `load()`) — no new API,
  // no vendor collection, no supplier profiles. Case/whitespace-normalized
  // dedupe; sorted alphabetically. The <datalist> below lets operators pick
  // an existing vendor OR type an entirely new one.
  const vendorSuggestions = useMemo(() => {
    const seen = new Map();
    for (const po of pos || []) {
      const raw = String(po?.vendor || "").trim();
      if (!raw) continue;
      const key = raw.toLowerCase();
      if (!seen.has(key)) seen.set(key, raw);
    }
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [pos]);

  const tierProducts = productsByTier[draft.tier] || [];

  const preview = useMemo(() => computeCpvPreview(draft.lines, draft.shipping_cost), [draft.lines, draft.shipping_cost]);
  const grandTotal = preview.reduce((s, p) => s + p.line_total, 0);
  const totalQty = draft.lines.reduce((s, l) => s + Math.max(0, Number(l.qty) || 0), 0);

  const setLine = (idx, field, value) => {
    setDraft((d) => {
      const lines = [...d.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      // iter203.5 — When product changes, auto-resolve variant_mg for
      // single-strength products. Resolution order:
      //   1. variants[0].mg   — modern single-variant Supply/Select
      //   2. kit_vial_strength_mg — canonical Supply strength field
      //   3. leading numeric prefix of dose_mg (e.g. "50mg" → "50") —
      //      legacy zero-variant Supply products that predate
      //      kit_vial_strength_mg (BBK-5A1-50, BBK-ADA, BBK-NAD-*, etc.)
      // Multi-variant products fall through to blank so the operator
      // still has to pick from the mg selector.
      if (field === "product_id") {
        const prod = products.find((p) => p.id === value);
        const vs = (prod && Array.isArray(prod.variants)) ? prod.variants : [];
        let resolvedMg = "";
        if (vs.length === 1 && String(vs[0].mg ?? "").trim() !== "") {
          resolvedMg = String(vs[0].mg).trim();
        } else if (vs.length === 0 && prod) {
          const kvs = String(prod.kit_vial_strength_mg ?? "").trim();
          if (kvs !== "") {
            resolvedMg = kvs;
          } else {
            const m = String(prod.dose_mg ?? "").trim().match(/^(\d+(?:\.\d+)?)/);
            if (m) resolvedMg = m[1];
          }
        }
        lines[idx].variant_mg = resolvedMg;
      }
      return { ...d, lines };
    });
  };

  const addLine = () => setDraft((d) => ({ ...d, lines: [...d.lines, emptyLine()] }));
  const removeLine = (idx) => setDraft((d) => ({ ...d, lines: d.lines.filter((_, i) => i !== idx) }));

  const canSubmit = draft.vendor.trim() && draft.lines.length > 0 && draft.lines.every((l) => (l.product_id || (l.custom_name || "").trim()) && Number(l.qty) > 0 && Number(l.product_cost) >= 0);

  // iter156.14 — modal draft helpers used by openEdit/onOpenChange.
  const resetDraft = () => setDraft({
    vendor: "",
    po_date: new Date().toISOString().slice(0, 10),
    shipping_cost: 0,
    tier: "supply",
    notes: "",
    lines: [emptyLine()],
  });
  const openEdit = (po) => {
    setEditingId(po.id);
    setDraft({
      vendor: po.vendor || "",
      po_date: po.po_date || new Date().toISOString().slice(0, 10),
      shipping_cost: po.shipping_cost || 0,
      tier: po.tier || "supply",
      notes: po.notes || "",
      lines: (po.lines || []).map((l) => ({
        product_id: l.product_id || "",
        variant_mg: l.variant_mg || "",
        custom_name: l.custom_name || "",
        custom_mg: l.custom_mg || "",
        qty: l.qty,
        product_cost: l.product_cost,
        testing_cost: l.testing_cost,
        misc_cost: l.misc_cost,
      })),
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const body = {
        vendor: draft.vendor.trim(),
        po_date: draft.po_date,
        shipping_cost: Number(draft.shipping_cost) || 0,
        tier: draft.tier,
        notes: draft.notes.trim() || null,
        lines: draft.lines.map((l) => ({
          product_id: l.product_id || null,
          variant_mg: l.variant_mg || null,
          custom_name: l.custom_name || null,
          custom_mg: l.custom_mg || null,
          qty: Number(l.qty),
          product_cost: Number(l.product_cost) || 0,
          testing_cost: Number(l.testing_cost) || 0,
          misc_cost: Number(l.misc_cost) || 0,
        })),
      };
      const { data } = editingId
        ? await api.put(`/admin/purchase-orders/${editingId}`, body)
        : await api.post("/admin/purchase-orders", body);
      toast.success(editingId ? "CPV updated" : `PO ${data.po_number} created`);
      setDialogOpen(false);
      setEditingId(null);
      setDraft({
        vendor: "",
        po_date: new Date().toISOString().slice(0, 10),
        shipping_cost: 0,
        tier: "supply",
        notes: "",
        lines: [emptyLine()],
      });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (po, status) => {
    try {
      await api.patch(`/admin/purchase-orders/${po.id}/status`, { status });
      toast.success(`PO ${po.po_number} → ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Status update failed");
    }
  };

  // Phase 1A — CPV → IMS receiving bridge.
  // Client-side counts are best-effort so the operator gets accurate
  // confirmation copy; the backend preflight is authoritative and will
  // reject the whole receive if anything is malformed.
  const onMarkReceived = async (po) => {
    const lines = po.lines || [];
    const real = lines.filter((l) => l.product_id && !l.custom_name).length;
    const adhoc = lines.filter((l) => !l.product_id && l.custom_name).length;
    const other = lines.length - real - adhoc;
    const lotsWord = (n) => `${n} inventory lot${n === 1 ? "" : "s"}`;
    const linesWord = (n, kind) => `${n} ${kind} line${n === 1 ? "" : "s"}`;
    let msg;
    if (real === 0) {
      // No catalog-linked lines → operator-friendly copy that explains the
      // no-op instead of using developer wording ("ad-hoc").
      msg = `Receive Purchase ${po.po_number}?\n\n` +
        `NO CATALOG SKU LINKED\n` +
        `0 vials will be added to IMS.\n\n` +
        `Link this item to a catalog SKU before receiving if you want it added to inventory.`;
    } else {
      msg = `Receive Purchase ${po.po_number}?\n\n` +
        `${linesWord(real, "catalog")} will be added to IMS.\n` +
        `${linesWord(adhoc, "ad-hoc pricing")} will not affect inventory.`;
    }
    if (other > 0) {
      msg += `\n\n⚠ ${linesWord(other, "incomplete")} — server will report exact errors.`;
    }
    msg += `\n\nThis cannot be undone from this screen.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.patch(`/admin/purchase-orders/${po.id}/status`, { status: "received" });
      const rr = data.receive_result || {};
      const created = (rr.lots_created || []).length;
      const skippedIdempotent = (rr.lots_skipped_idempotent || []).length;
      const adHocSkipped = (rr.ad_hoc_lines_skipped || []).length;
      let out = `${lotsWord(created)} added to IMS.`;
      if (skippedIdempotent) out += ` ${skippedIdempotent} line${skippedIdempotent === 1 ? "" : "s"} already received (idempotent).`;
      if (adHocSkipped) out += ` ${adHocSkipped} ad-hoc line${adHocSkipped === 1 ? "" : "s"} skipped.`;
      toast.success(out, { duration: 6000 });
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        const errs = detail.errors
          .map((er) => `Line ${er.line_index + 1}: ${er.detail}`)
          .join("\n");
        toast.error(`Receive rejected. PO stays in draft.\n${errs}`, { duration: 10000 });
      } else {
        toast.error(formatApiErrorDetail(e) || "Receive failed");
      }
    }
  };

  const [viewMargin, setViewMargin] = useState(null);
  const [viewMarginLoading, setViewMarginLoading] = useState(false);
  const [viewHypo, setViewHypo] = useState("");
  const [viewHypoDebounced, setViewHypoDebounced] = useState("");
  // iter200 Phase 1B — CPV → margin sandbox (Select only, read-only).
  // Fetch CURRENT on dialog open, re-fetch with hypothetical after debounce.
  useEffect(() => {
    if (!viewOpen || !viewing) { setViewMargin(null); setViewHypo(""); setViewHypoDebounced(""); return; }
    let cancelled = false;
    const load = async () => {
      setViewMarginLoading(true);
      try {
        const params = viewHypoDebounced ? { hypothetical_vial_price: viewHypoDebounced } : {};
        const { data } = await api.get(`/admin/purchase-orders/${viewing.id}/margin`, { params });
        if (!cancelled) setViewMargin(data);
      } catch (e) {
        if (!cancelled) {
          const detail = e?.response?.data?.detail;
          if (typeof detail === "string") toast.error(detail);
          setViewMargin({ lines: [], error: detail || "Failed to load margin" });
        }
      } finally {
        if (!cancelled) setViewMarginLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [viewOpen, viewing, viewHypoDebounced]);
  useEffect(() => {
    const t = setTimeout(() => setViewHypoDebounced(viewHypo), 300);
    return () => clearTimeout(t);
  }, [viewHypo]);

  const deletePO = async (po) => {
    if (!confirm(`Delete PO ${po.po_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/purchase-orders/${po.id}`);
      toast.success(`PO ${po.po_number} deleted`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Delete failed");
    }
  };

  const statusColor = {
    draft: "bg-bone border-borderSubtle text-charcoal",
    received: "bg-emerald-50 border-emerald-600/40 text-emerald-700",
    cancelled: "bg-oxblood/5 border-oxblood text-oxblood",
  };

  const findVariants = (productId) => {
    const p = products.find((x) => x.id === productId);
    return Array.isArray(p?.variants) ? p.variants : [];
  };

  // iter156.12 — slug helper + quick-create submit.
  const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").slice(0, 60);
  const openQuickNew = (idx) => {
    setQuickTargetLineIdx(idx);
    setQuickNew(emptyQuickNew());
    setQuickNewOpen(true);
  };
  const submitQuickNew = async () => {
    const name = quickNew.name.trim();
    if (!name) { toast.error("Name required"); return; }
    // iter156.13 — CPV is admin-only. Ad-hoc products live ONLY on the CPV
    // form; nothing is created in the catalog. `custom_name` + `custom_mg`
    // travel with the line to the backend for storage / recall.
    if (quickTargetLineIdx != null) {
      setLine(quickTargetLineIdx, "product_id", "");
      setLine(quickTargetLineIdx, "variant_mg", "");
      setLine(quickTargetLineIdx, "custom_name", name);
      setLine(quickTargetLineIdx, "custom_mg", quickNew.mg.trim());
    }
    setQuickNewOpen(false);
  };

  return (
    <div data-testid="admin-purchase-orders">
      <div className="mb-6 border border-borderSubtle bg-bone px-5 py-4">
        <p className="text-[13px] text-charcoal leading-relaxed">
          <span className="font-mono uppercase tracking-[0.22em] text-oxblood mr-2">Vendor ledger</span>
          Track incoming shipments and auto-compute Cost Per Vial (CPV) with shipping allocated proportionally across every line. Feeds the Affiliates margin split later.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 border border-borderSubtle hover:border-charcoal text-charcoal text-[12px] uppercase tracking-[0.22em] rounded-full transition-colors"
          data-testid="po-refresh-btn"
        >
          <RefreshCw size={12} strokeWidth={2} /> Refresh
        </button>
        <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditingId(null); resetDraft(); } }}>
          <DialogTrigger asChild>
            <button
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-oxblood text-bone hover:bg-charcoal text-[13px] uppercase tracking-[0.22em] rounded-full transition-colors"
              data-testid="po-new-btn"
            >
              <Plus size={12} strokeWidth={2} /> New CPV entry
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-[min(1400px,96vw)] w-[96vw] rounded-none bg-bone border-borderSubtle max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl">{editingId ? "Edit CPV entry" : "New CPV entry"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-1.5">Vendor</label>
                  <Input
                    value={draft.vendor}
                    onChange={(e) => setDraft({ ...draft, vendor: e.target.value })}
                    placeholder="Vendor name"
                    data-testid="po-vendor-input"
                    list="po-vendor-suggestions"
                    autoComplete="off"
                  />
                  {/* iter203.5 — vendor autocomplete backed by DISTINCT
                      historical vendors from purchase_orders. Native
                      <datalist> so we add zero new UI dependencies. */}
                  <datalist id="po-vendor-suggestions">
                    {(vendorSuggestions || []).map((v) => (
                      <option key={v} value={v} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="overline block mb-1.5">Tier</label>
                  <Select value={draft.tier} onValueChange={(v) => setDraft({ ...draft, tier: v, lines: [emptyLine()] })}>
                    <SelectTrigger data-testid="po-tier-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="supply">Supply</SelectItem>
                      <SelectItem value="select">Select</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="overline block mb-1.5">Date</label>
                  <Input
                    type="date"
                    value={draft.po_date}
                    onChange={(e) => setDraft({ ...draft, po_date: e.target.value })}
                    data-testid="po-date-input"
                  />
                </div>
                <div>
                  <label className="overline block mb-1.5">Shipping (freight) $</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={draft.shipping_cost}
                    onChange={(e) => setDraft({ ...draft, shipping_cost: e.target.value })}
                    data-testid="po-shipping-input"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="overline">Line items</label>
                  <button
                    onClick={addLine}
                    className="inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.22em] text-oxblood hover:text-charcoal transition-colors"
                    data-testid="po-add-line-btn"
                  >
                    <Plus size={12} /> Add line
                  </button>
                </div>
                <div className="border border-borderSubtle bg-white overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead className="bg-bone border-b border-borderSubtle">
                      <tr className="text-left">
                        {["Product", "mg", "Qty", "Product $", "Test $", "Misc $", "Ship share", "CPV", ""].map((h) => (
                          <th key={h} className="overline px-2 py-2 text-charcoal">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {draft.lines.map((l, idx) => {
                        const variants = findVariants(l.product_id);
                        const p = preview[idx] || { shipping_share: 0, line_total: 0, cpv: 0 };
                        return (
                          <tr key={idx} className="border-b border-borderSubtle last:border-0" data-testid={`po-line-${idx}`}>
                            <td className="px-2 py-1.5">
                              {l.custom_name ? (
                                // iter156.13 — Ad-hoc line: name typed inline, not in catalog.
                                <div
                                  className="h-9 min-w-[220px] flex items-center gap-2 px-3 border border-oxblood/40 bg-oxblood/5 text-[13px] rounded-md"
                                  data-testid={`po-line-${idx}-custom`}
                                >
                                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-oxblood">Custom</span>
                                  <span className="text-charcoal truncate">{l.custom_name}</span>
                                  <button
                                    onClick={() => { setLine(idx, "custom_name", ""); setLine(idx, "custom_mg", ""); }}
                                    className="ml-auto text-charcoal/50 hover:text-oxblood"
                                    title="Clear custom entry"
                                    data-testid={`po-line-${idx}-custom-clear`}
                                  >
                                    <XCircle size={13} />
                                  </button>
                                </div>
                              ) : (
                                <Select value={l.product_id} onValueChange={(v) => {
                                  if (v === "__new__") { openQuickNew(idx); return; }
                                  setLine(idx, "product_id", v);
                                }}>
                                  <SelectTrigger className="h-9 min-w-[220px] text-[13px]" data-testid={`po-line-${idx}-product`}>
                                    <SelectValue placeholder="Pick product…" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-72">
                                    <SelectItem value="__new__" className="text-oxblood font-medium" data-testid={`po-line-${idx}-add-new-product`}>
                                      + New product (ad-hoc)…
                                    </SelectItem>
                                    {tierProducts.length > 0 && (
                                      <div className="border-t border-borderSubtle my-1" />
                                    )}
                                    {tierProducts.map((p) => (
                                      <SelectItem key={p.id} value={p.id}>
                                        {p.sku} · {p.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="px-2 py-1.5">
                              {l.custom_name ? (
                                // Custom line: show the mg the admin typed in the mini modal.
                                <span className="font-mono text-[13px] text-charcoal">{l.custom_mg || "—"}</span>
                              ) : variants.length === 1 ? (
                                // iter203.5 — Single-strength product: mg is a canonical fact of
                                // the product record, not an operator decision. Render read-only.
                                <span
                                  className="font-mono text-[13px] text-charcoal"
                                  data-testid={`po-line-${idx}-mg-fixed`}
                                  title="Resolved from product (single strength)"
                                >
                                  {variants[0].mg}mg
                                </span>
                              ) : variants.length > 1 ? (
                                <Select value={l.variant_mg} onValueChange={(v) => setLine(idx, "variant_mg", v)}>
                                  <SelectTrigger className="h-9 w-20 text-[13px]" data-testid={`po-line-${idx}-mg`}>
                                    <SelectValue placeholder="—" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {variants.map((v) => (
                                      <SelectItem key={v.mg} value={String(v.mg)}>{v.mg}mg</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : l.variant_mg ? (
                                // iter203.5 — Zero-variant single-strength Supply: mg was
                                // auto-resolved from kit_vial_strength_mg or dose_mg on
                                // product select. Render read-only, matching the
                                // variants.length === 1 UX.
                                <span
                                  className="font-mono text-[13px] text-charcoal"
                                  data-testid={`po-line-${idx}-mg-fixed`}
                                  title="Resolved from product (single strength)"
                                >
                                  {l.variant_mg}mg
                                </span>
                              ) : (
                                <span className="text-charcoal/50 text-[12px]">—</span>
                              )}
                            </td>
                            <td className="px-2 py-1.5">
                              <Input
                                type="number"
                                min="1"
                                value={l.qty}
                                onChange={(e) => setLine(idx, "qty", e.target.value)}
                                className="h-9 w-20 text-[13px]"
                                data-testid={`po-line-${idx}-qty`}
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={l.product_cost}
                                onChange={(e) => setLine(idx, "product_cost", e.target.value)}
                                className="h-9 w-24 text-[13px]"
                                data-testid={`po-line-${idx}-product-cost`}
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={l.testing_cost}
                                onChange={(e) => setLine(idx, "testing_cost", e.target.value)}
                                className="h-9 w-20 text-[13px]"
                                data-testid={`po-line-${idx}-testing-cost`}
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={l.misc_cost}
                                onChange={(e) => setLine(idx, "misc_cost", e.target.value)}
                                className="h-9 w-20 text-[13px]"
                                data-testid={`po-line-${idx}-misc-cost`}
                              />
                            </td>
                            <td className="px-2 py-1.5 font-mono text-[12px] text-charcoal/75" data-testid={`po-line-${idx}-share`}>
                              ${p.shipping_share.toFixed(2)}
                            </td>
                            <td className="px-2 py-1.5 font-mono text-[13px] text-oxblood font-bold" data-testid={`po-line-${idx}-cpv`}>
                              ${p.cpv.toFixed(4)}
                            </td>
                            <td className="px-2 py-1.5">
                              {draft.lines.length > 1 && (
                                <button
                                  onClick={() => removeLine(idx)}
                                  className="text-charcoal/60 hover:text-oxblood"
                                  data-testid={`po-line-${idx}-remove`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="text-right mt-2 space-y-0.5">
                  <div className="text-[13px] text-charcoal/75">Total qty: <span className="font-mono">{totalQty}</span></div>
                  <div className="text-[15px] text-charcoal font-mono font-bold" data-testid="po-grand-total">Grand total: ${grandTotal.toFixed(2)}</div>
                </div>
              </div>

              <div>
                <label className="overline block mb-1.5">Notes</label>
                <Textarea
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  placeholder="Lot #, batch notes, next-time reminders…"
                  className="min-h-[80px]"
                  data-testid="po-notes-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-borderSubtle">
                <button
                  onClick={() => setDialogOpen(false)}
                  className="px-4 py-2 border border-borderSubtle text-charcoal text-[13px] uppercase tracking-[0.22em] rounded-full"
                  data-testid="po-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={!canSubmit || saving}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-oxblood text-bone hover:bg-charcoal text-[13px] uppercase tracking-[0.22em] rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="po-submit-btn"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Package size={12} />}
                  {saving ? "Saving…" : (editingId ? "Save changes" : "Create CPV")}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-borderSubtle bg-white overflow-x-auto" data-testid="po-table">
        {loading && pos.length === 0 ? (
          <div className="p-8 text-center text-sm text-charcoal/75">Loading…</div>
        ) : pos.length === 0 ? (
          <div className="p-8 text-center text-sm text-charcoal/75">No purchase orders yet.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-bone border-b border-borderSubtle">
              <tr className="text-left">
                {["Vendor", "Product", "Date", "Tier", "Qty", "Total", "CPV", ""].map((h) => (
                  <th key={h} className="overline px-4 py-3 text-charcoal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pos.map((po) => {
                const avgCpv = po.total_qty > 0 ? po.total_cost / po.total_qty : 0;
                // iter203.2 — CPV summary "Product" column. Reads the
                // server-materialized `lines[N].name` label (already stored
                // as "{ProductName} · {mg}mg" for catalog lines and
                // "{custom_name} · {custom_mg}mg" for ad-hoc lines — see
                // routes/purchase_orders.py::_resolve_line_meta). No new
                // API; pure read of what's already on the PO doc.
                const lines = Array.isArray(po.lines) ? po.lines : [];
                const first = lines[0] || null;
                const firstLabel = first ? (first.name || first.sku || "—") : "—";
                const extra = lines.length > 1 ? ` + ${lines.length - 1} more` : "";
                return (
                <tr key={po.id} className="border-b border-borderSubtle last:border-0" data-testid={`po-row-${po.po_number}`}>
                  <td className="px-4 py-3 text-charcoal">{po.vendor}</td>
                  <td className="px-4 py-3 text-charcoal" data-testid={`po-product-${po.po_number}`}>
                    <span
                      className="block max-w-[220px] truncate"
                      title={lines.length > 1 ? `${firstLabel}${extra}` : firstLabel}
                    >
                      {firstLabel}{extra}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-charcoal/75 font-mono text-[12px]">{po.po_date}</td>
                  <td className="px-4 py-3 font-mono text-[12px] uppercase tracking-[0.22em]">{po.tier}</td>
                  <td className="px-4 py-3 font-mono text-[12px]">{po.total_qty}</td>
                  <td className="px-4 py-3 font-mono text-[13px] text-charcoal">${po.total_cost.toFixed(2)}</td>
                  <td className="px-4 py-3 font-mono text-[13px] text-oxblood" data-testid={`po-cpv-${po.po_number}`}>${avgCpv.toFixed(4)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {po.status === "received" ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.22em] text-moss border border-mossBorder bg-mossBg rounded-none"
                          title={`Received into IMS${po.status_at ? ` at ${po.status_at}` : ""}`}
                          data-testid={`po-received-badge-${po.po_number}`}
                        >
                          <PackageCheck size={11} strokeWidth={2} /> Received
                        </span>
                      ) : po.status !== "cancelled" ? (
                        <button
                          onClick={() => onMarkReceived(po)}
                          className="text-moss hover:text-charcoal"
                          title="Receive into IMS — creates one inventory lot per catalog line"
                          data-testid={`po-receive-${po.po_number}`}
                        >
                          <PackageCheck size={16} strokeWidth={2} />
                        </button>
                      ) : null}
                      <button
                        onClick={() => { setViewing(po); setViewOpen(true); }}
                        className="text-charcoal/70 hover:text-charcoal"
                        title="View CPV details"
                        data-testid={`po-view-${po.po_number}`}
                      >
                        <Eye size={15} />
                      </button>
                      <button
                        onClick={() => openEdit(po)}
                        className="text-charcoal/70 hover:text-charcoal"
                        title="Edit CPV entry"
                        data-testid={`po-edit-${po.po_number}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => deletePO(po)}
                        className="text-charcoal/60 hover:text-oxblood"
                        title="Delete"
                        data-testid={`po-delete-${po.po_number}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={quickNewOpen} onOpenChange={setQuickNewOpen}>
        <DialogContent className="max-w-lg rounded-none bg-bone border-borderSubtle">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Quick-add product</DialogTitle>
          </DialogHeader>
          <div className="pt-3 space-y-4">
            <p className="text-[13px] text-charcoal/75 leading-relaxed">
              Adds an <span className="font-mono">ad-hoc</span> line to this CPV calc only. Nothing is saved to your catalog — this is for pricing peptides you're evaluating or one-off vendor buys. Type a name and (optional) mg, then hit "Add to CPV line".
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="overline block mb-1.5">Product name</label>
                <Input
                  value={quickNew.name}
                  onChange={(e) => setQuickNew({ ...quickNew, name: e.target.value })}
                  placeholder="e.g. BPC-157"
                  data-testid="quick-new-name"
                />
              </div>
              <div>
                <label className="overline block mb-1.5">mg (dose)</label>
                <Input
                  value={quickNew.mg}
                  onChange={(e) => setQuickNew({ ...quickNew, mg: e.target.value })}
                  placeholder="e.g. 10"
                  data-testid="quick-new-mg"
                />
              </div>
              <div>
                <label className="overline block mb-1.5">Tier (from CPV)</label>
                <div className="h-9 flex items-center px-3 border border-borderSubtle bg-white font-mono text-[13px] uppercase tracking-[0.22em] text-charcoal/75" data-testid="quick-new-tier">
                  {draft.tier}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-borderSubtle">
              <button
                onClick={() => setQuickNewOpen(false)}
                className="px-4 py-2 border border-borderSubtle text-charcoal text-[13px] uppercase tracking-[0.22em] rounded-full"
                data-testid="quick-new-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitQuickNew}
                disabled={!quickNew.name.trim()}
                className="inline-flex items-center gap-2 px-5 py-2 bg-oxblood text-bone hover:bg-charcoal text-[13px] uppercase tracking-[0.22em] rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="quick-new-submit"
              >
                <Plus size={12} /> Add to CPV line
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-[min(1400px,96vw)] w-[96vw] rounded-none bg-bone border-borderSubtle max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{viewing?.vendor || "CPV entry"}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4 pt-3" data-testid="po-view-body">
              <div className="grid grid-cols-3 gap-3 text-[13px]">
                <div><div className="overline">Date</div><div className="font-mono">{viewing.po_date}</div></div>
                <div><div className="overline">Tier</div><div className="font-mono uppercase">{viewing.tier}</div></div>
                <div><div className="overline">Shipping</div><div className="font-mono">${viewing.shipping_cost.toFixed(2)}</div></div>
              </div>
              <div className="border border-borderSubtle bg-white overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="bg-bone border-b border-borderSubtle">
                    <tr className="text-left">
                      {["SKU", "Item", "Qty", "Product $", "Test $", "Misc $", "Ship share", "Line total", "CPV"].map((h) => (
                        <th key={h} className="overline px-3 py-2 text-charcoal">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {viewing.lines.map((l, i) => (
                      <tr key={i} className="border-b border-borderSubtle last:border-0">
                        <td className="px-3 py-2 font-mono text-[12px]">{l.sku}</td>
                        <td className="px-3 py-2">{l.name}</td>
                        <td className="px-3 py-2 font-mono">{l.qty}</td>
                        <td className="px-3 py-2 font-mono">${l.product_cost.toFixed(2)}</td>
                        <td className="px-3 py-2 font-mono">${l.testing_cost.toFixed(2)}</td>
                        <td className="px-3 py-2 font-mono">${l.misc_cost.toFixed(2)}</td>
                        <td className="px-3 py-2 font-mono text-charcoal/75">${l.shipping_share.toFixed(2)}</td>
                        <td className="px-3 py-2 font-mono">${l.line_total.toFixed(2)}</td>
                        <td className="px-3 py-2 font-mono text-oxblood font-bold">${l.cpv.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-right font-mono text-[15px] font-bold text-charcoal">
                Total: ${viewing.total_cost.toFixed(2)} · {viewing.total_qty} vials
              </div>
              {viewing.notes && (
                <div className="border-l-4 border-oxblood bg-white p-4 text-[13px] text-charcoal">
                  <div className="overline mb-1">Notes</div>
                  <div className="whitespace-pre-wrap">{viewing.notes}</div>
                </div>
              )}

              {/* Phase 1B — Pricing & margin sandbox (read-only). Renders
                  for both Select (existing) and Supply (iter203.5) lines. */}
              {(viewMargin?.lines || []).some(l => l.tier === "select" || l.tier === "supply") && (
                <div className="border border-borderSubtle bg-white p-5 space-y-4" data-testid="po-margin-panel">
                  <div className="flex items-baseline justify-between gap-4">
                    <div>
                      <div className="overline">Pricing & margin</div>
                      <div className="text-[12px] text-charcoal/60 leading-relaxed">
                        Uses the live checkout resolver — no writes. Supply lines use Assigned Tier × Vials Per Kit; Select lines use the current ladder + variant.
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-[12px] text-charcoal/75">
                      <span className="whitespace-nowrap font-mono uppercase tracking-[0.22em]">What-if $/vial · Select only</span>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={viewHypo}
                        onChange={(e) => setViewHypo(e.target.value)}
                        placeholder="—"
                        className="h-8 w-28 text-[13px]"
                        data-testid="po-margin-hypo-input"
                      />
                      {viewMarginLoading && <Loader2 size={12} className="animate-spin text-charcoal/50" />}
                    </label>
                  </div>
                  <div className="space-y-3">
                    {(viewMargin?.lines || []).filter(l => l.tier === "select").map((l) => (
                      <MarginLineRow
                        key={l.line_index}
                        line={l}
                        hypoActive={!!viewHypoDebounced}
                        hypoValue={viewHypoDebounced}
                        poId={viewing.id}
                        onPushed={() => { setViewHypo(""); setViewHypoDebounced(""); }}
                      />
                    ))}
                    {(viewMargin?.lines || []).filter(l => l.tier === "supply").map((l) => (
                      <SupplyMarginRow key={l.line_index} line={l} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SupplyMarginRow({ line }) {
  // iter203.5 — Read-only Supply margin card. Every value comes from
  // /admin/purchase-orders/{po_id}/margin (see _compute_supply_margin_block).
  const cur = line.current || {};
  const fmtUsd = (x) => (x === null || x === undefined ? "—" : `$${Number(x).toFixed(2)}`);
  const fmtPct = (x) => (x === null || x === undefined ? "—" : `${Number(x).toFixed(1)}%`);
  const marginInk = cur.kit_margin_pct == null
    ? "text-charcoal/50"
    : cur.kit_margin_pct >= 0 ? "text-moss" : "text-oxblood";
  return (
    <div
      className="grid grid-cols-6 gap-3 items-baseline border border-borderSubtle rounded-md px-3 py-2.5 bg-bone/40"
      data-testid={`supply-margin-row-${line.line_index}`}
    >
      <div className="col-span-2">
        <div className="text-[13px] text-charcoal font-medium truncate" title={line.name}>{line.name}</div>
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-charcoal/60">
          Supply · {cur.tier_label || "—"} · {cur.vials_per_kit || "—"} vials/kit
        </div>
      </div>
      <div>
        <div className="overline text-[10px]">CPV / vial</div>
        <div className="font-mono text-[13px] text-charcoal" data-testid={`supply-margin-cpv-${line.line_index}`}>
          ${Number(line.cpv || 0).toFixed(4)}
        </div>
      </div>
      <div>
        <div className="overline text-[10px]">Landed kit</div>
        <div className="font-mono text-[13px] text-charcoal" data-testid={`supply-margin-landed-${line.line_index}`}>
          {fmtUsd(cur.landed_kit_cost)}
        </div>
      </div>
      <div>
        <div className="overline text-[10px]">Kit price</div>
        <div className="font-mono text-[13px] text-charcoal" data-testid={`supply-margin-price-${line.line_index}`}>
          {fmtUsd(cur.kit_price)}
        </div>
      </div>
      <div>
        <div className="overline text-[10px]">Kit profit · margin</div>
        <div className={`font-mono text-[13px] ${marginInk}`} data-testid={`supply-margin-profit-${line.line_index}`}>
          {fmtUsd(cur.kit_profit)} · {fmtPct(cur.kit_margin_pct)}
        </div>
      </div>
    </div>
  );
}

function MarginLineRow({ line, hypoActive, hypoValue, poId, onPushed }) {
  const [pushing, setPushing] = useState(false);
  const fmtPct = (x) => (x === null || x === undefined ? "—" : `${x.toFixed(1)}%`);
  const fmtUsd = (x) => (x === null || x === undefined ? "—" : `$${Number(x).toFixed(2)}`);
  const marginInk = (pct) => (pct == null ? "text-charcoal/50" : pct >= 0 ? "text-moss" : "text-oxblood");
  const currentUnit = line.current?.per_vial?.kit_price;
  const hypoNum = hypoActive && hypoValue ? parseFloat(hypoValue) : null;
  const canPush = (
    line.tier === "select" &&
    line.variant_mg &&
    currentUnit != null &&
    hypoNum != null && isFinite(hypoNum) && hypoNum > 0 &&
    Math.abs(hypoNum - currentUnit) > 0.005
  );

  const doPush = async () => {
    if (!canPush || !line.hypothetical) return;
    const h = line.hypothetical;
    const msg =
      `Push Select price to storefront?\n\n` +
      `Product:  ${line.name}\n` +
      `Strength: ${line.variant_mg}mg\n\n` +
      `Current:  $${currentUnit.toFixed(2)} / vial\n` +
      `New:      $${hypoNum.toFixed(2)} / vial\n\n` +
      `New storefront prices:\n` +
      `  1 vial:  $${h.per_vial ? h.per_vial.kit_price.toFixed(2) : "—"}\n` +
      (h.kit_3vial ? `  3 vials: $${h.kit_3vial.kit_price.toFixed(2)}\n` : "") +
      (h.kit_5vial ? `  5 vials: $${h.kit_5vial.kit_price.toFixed(2)}\n` : "") +
      (h.kit_10vial ? `  10 vials: $${h.kit_10vial.kit_price.toFixed(2)}\n` : "") +
      `\nThis takes effect immediately for buyers.\nContinue?`;
    if (!window.confirm(msg)) return;
    setPushing(true);
    try {
      const body = {
        variant_mg: line.variant_mg,
        expected_current_price: currentUnit,
        new_price: hypoNum,
        po_id: poId,
        po_line_index: line.line_index,
      };
      const { data } = await api.post(`/admin/products/${line.product_id}/select-variant-price`, body);
      toast.success(`Storefront updated. ${data.variant_mg}mg is now $${data.new_price.toFixed(2)} / vial.`);
      if (onPushed) onPushed();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && typeof detail === "object") {
        toast.error(
          `Price changed elsewhere — was $${detail.actual_current_price?.toFixed(2) ?? "?"}, ` +
          `you saw $${detail.expected_current_price?.toFixed(2) ?? "?"}. Refresh and try again.`,
          { duration: 8000 }
        );
        if (onPushed) onPushed();
      } else {
        toast.error(typeof detail === "string" ? detail : "Push failed.");
      }
    } finally {
      setPushing(false);
    }
  };

  const Row = ({ label, block }) => {
    if (!block) {
      return (
        <tr>
          <td className="px-3 py-1.5 text-[12px] font-mono uppercase tracking-[0.22em] text-charcoal/60">{label}</td>
          <td colSpan={4} className="px-3 py-1.5 text-[12px] text-charcoal/50 italic">not sold on storefront</td>
        </tr>
      );
    }
    return (
      <tr>
        <td className="px-3 py-1.5 text-[12px] font-mono uppercase tracking-[0.22em] text-charcoal/75">{label}</td>
        <td className="px-3 py-1.5 font-mono text-[13px] text-charcoal">{fmtUsd(block.kit_price)}</td>
        <td className="px-3 py-1.5 font-mono text-[13px] text-charcoal/75">{fmtUsd(block.landed_cost)}</td>
        <td className={`px-3 py-1.5 font-mono text-[13px] font-medium ${marginInk(block.gross_margin_pct)}`}>{fmtUsd(block.gross_profit)}</td>
        <td className={`px-3 py-1.5 font-mono text-[13px] font-bold ${marginInk(block.gross_margin_pct)}`}>{fmtPct(block.gross_margin_pct)}</td>
      </tr>
    );
  };
  return (
    <div className="border border-borderSubtle" data-testid={`po-margin-line-${line.line_index}`}>
      <div className="bg-bone px-3 py-2 border-b border-borderSubtle text-[12px] flex items-baseline gap-3 flex-wrap">
        <span className="font-mono text-charcoal">{line.name}</span>
        {line.variant_mg && <span className="font-mono text-[11px] text-charcoal/60">{line.variant_mg}mg</span>}
        {line.single_buy && <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-oxblood">single-buy</span>}
        <span className="ml-auto font-mono text-[11px] text-charcoal/60">
          CPV <span className="text-oxblood font-bold">${Number(line.cpv).toFixed(4)}</span> · ladder 3v {(line.ladder_used[3]*100).toFixed(0)}% ({line.ladder_source[3]}), 5v {(line.ladder_used[5]*100).toFixed(0)}% ({line.ladder_source[5]})
        </span>
      </div>
      <table className="w-full">
        <thead>
          <tr className="text-left border-b border-borderSubtle">
            <th className="px-3 py-1.5 overline text-charcoal">Tier</th>
            <th className="px-3 py-1.5 overline text-charcoal">Storefront $</th>
            <th className="px-3 py-1.5 overline text-charcoal">Landed $</th>
            <th className="px-3 py-1.5 overline text-charcoal">Profit $</th>
            <th className="px-3 py-1.5 overline text-charcoal">Margin %</th>
          </tr>
        </thead>
        <tbody className="bg-white">
          <Row label="Current · 1v" block={line.current?.per_vial} />
          <Row label="Current · 3v" block={line.current?.kit_3vial} />
          <Row label="Current · 5v" block={line.current?.kit_5vial} />
          <Row label="Current · 10v" block={line.current?.kit_10vial} />
          {hypoActive && line.hypothetical && (
            <>
              <tr><td colSpan={5} className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-[0.22em] text-oxblood border-t border-borderSubtle">What-if</td></tr>
              <Row label="What-if · 1v" block={line.hypothetical?.per_vial} />
              <Row label="What-if · 3v" block={line.hypothetical?.kit_3vial} />
              <Row label="What-if · 5v" block={line.hypothetical?.kit_5vial} />
              <Row label="What-if · 10v" block={line.hypothetical?.kit_10vial} />
            </>
          )}
        </tbody>
      </table>
      {hypoActive && (
        <div className="px-3 py-2 border-t border-borderSubtle flex items-center justify-end bg-bone">
          <button
            onClick={doPush}
            disabled={!canPush || pushing}
            className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] border rounded-none transition-colors ${
              canPush && !pushing
                ? "border-oxblood text-oxblood hover:bg-oxblood hover:text-bone cursor-pointer"
                : "border-charcoal/20 text-charcoal/40 cursor-not-allowed"
            }`}
            data-testid={`po-margin-push-${line.line_index}`}
            title={canPush ? "Push this price to the product's variant" : "Enter a different what-if $/vial to enable Push"}
          >
            {pushing ? "Pushing…" : `Push $${hypoNum ? hypoNum.toFixed(2) : "—"} to product`}
          </button>
        </div>
      )}
    </div>
  );
}

